import {
  AlertTriangle,
  Banknote,
  Clock,
  FileText,
  Inbox,
  MessageSquare,
  RefreshCw,
  ListChecks,
  Send,
  UserSearch,
  TrendingDown,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import type { PriorityAction } from "@/api/types";

interface Props {
  action: PriorityAction;
  onAction: (action: PriorityAction) => void;
}

/** iconType whitelist (FIELD_MATRIX §7 + v1.2.2 R1 types) — unknown → FileText. */
const ICON_WHITELIST: Record<string, LucideIcon> = {
  AlertTriangle,
  Banknote, // payment_promise_due / payment_promise_broken
  Clock,
  FileText,
  Inbox, // internal_request_inbox
  MessageSquare, // internal_request_needs_info (#9 S4)
  RefreshCw,
  ListChecks,
  Send, // internal_request_waiting
  UserSearch,
  TrendingDown,
};

/** tone whitelist — unknown → slate. Each tone: left bar · icon chip · button. Dark-mode aware. */
const TONE: Record<
  string,
  { bar: string; chipBg: string; chipText: string; btn: string }
> = {
  red: {
    bar: "bg-destructive",
    chipBg: "bg-destructive-soft",
    chipText: "text-destructive-soft-foreground",
    btn: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  },
  amber: {
    bar: "bg-warning-foreground",
    chipBg: "bg-warning",
    chipText: "text-warning-foreground",
    btn: "bg-warning-foreground text-white hover:bg-warning-foreground/90",
  },
  blue: {
    bar: "bg-info-foreground",
    chipBg: "bg-info",
    chipText: "text-info-foreground",
    btn: "bg-info-foreground text-white hover:bg-info-foreground/90",
  },
  slate: {
    bar: "bg-muted-foreground",
    chipBg: "bg-muted",
    chipText: "text-muted-foreground",
    btn: "bg-foreground/80 text-background hover:bg-foreground/70",
  },
};

const safeTone = (t: string) => (TONE[t] ? t : "slate");
const safeIcon = (t: string): LucideIcon => ICON_WHITELIST[t] ?? FileText;

export function ActionRow({ action, onAction }: Props) {
  const tone = safeTone(action.tone);
  const t = TONE[tone];
  const Icon = safeIcon(action.iconType);
  const accountLine = action.accountName ?? action.contextLabel;

  return (
    <div className="flex min-h-[72px] items-stretch overflow-hidden rounded-xl border bg-card text-card-foreground">
      <div className={cn("w-1 shrink-0", t.bar)} aria-hidden />
      <div className="flex flex-1 flex-col gap-2 p-3.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className={cn("mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", t.chipBg)}>
            <Icon className={cn("h-[18px] w-[18px]", t.chipText)} strokeWidth={2} aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="inline-block rounded-md bg-muted px-1.5 py-0.5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
              {action.sourceModule}
            </span>
            <p className="mt-1 text-[15px] font-semibold leading-snug text-foreground">{action.title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{action.reason}</p>
            {accountLine && (
              <p className="mt-0.5 text-xs font-medium text-primary">
                {accountLine}
                {action.scope === "team" && action.assigneeName && (
                  <span className="ml-2 font-normal text-muted-foreground">· {action.assigneeName}</span>
                )}
              </p>
            )}
          </div>
        </div>
        <Button
          onClick={() => onAction(action)}
          className={cn("h-9 shrink-0 gap-1.5 sm:self-center", t.btn)}
        >
          {action.cta.label}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
